import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  BookOpen,
  Bot,
  ClipboardCheck,
  Eye,
  FileText,
  Globe,
  LayoutDashboard,
  LogOut,
  Mail,
  Search,
  Settings,
  ShieldCheck,
  Upload,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Suspense, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ROLE_LABEL, type Role } from "@/lib/roles";
import type { Dashboard } from "@/lib/types";
import { Empty, Loading } from "./ui";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  minimum: Role;
  end?: boolean;
  count?: (dashboard: Dashboard | undefined) => number | undefined;
}

const NAV: { label: string; items: NavItem[] }[] = [
  {
    label: "Workspace",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, minimum: "viewer", end: true },
      { to: "/content", label: "Content", icon: FileText, minimum: "viewer", end: true },
      {
        to: "/content?status=waiting_review",
        label: "Review queue",
        icon: ClipboardCheck,
        minimum: "editor",
        count: (d) => d?.content.waiting_review || undefined,
      },
    ],
  },
  {
    label: "Newsletter",
    items: [
      { to: "/newsletter", label: "Issues", icon: Mail, minimum: "editor", end: true },
      { to: "/newsletter/subscribers", label: "Subscribers", icon: Users, minimum: "admin" },
    ],
  },
  {
    label: "Magazine",
    items: [{ to: "/magazine", label: "Issues", icon: BookOpen, minimum: "viewer" }],
  },
  {
    label: "Operations",
    items: [
      { to: "/sources", label: "Sources", icon: Globe, minimum: "admin" },
      { to: "/agents", label: "Agents", icon: Bot, minimum: "admin" },
      { to: "/activity", label: "Activity", icon: Activity, minimum: "admin" },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/visitors", label: "Visitors", icon: Eye, minimum: "super_admin" },
      { to: "/users", label: "Users", icon: ShieldCheck, minimum: "super_admin" },
      { to: "/settings", label: "Website Configuration", icon: Settings, minimum: "super_admin" },
      { to: "/imports", label: "Imports", icon: Upload, minimum: "super_admin" },
    ],
  },
];

export function useDashboard() {
  return useQuery({ queryKey: ["dashboard"], queryFn: () => api<Dashboard>("/dashboard/"), staleTime: 30_000 });
}

export function Layout() {
  const { user, can, logout } = useAuth();
  const dashboard = useDashboard();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");

  // "Content" and "Review queue" share a path; the query string tells them apart.
  const inReview = location.pathname === "/content" && new URLSearchParams(location.search).get("status") === "waiting_review";
  const linkClass = (item: NavItem) => ({ isActive }: { isActive: boolean }) => {
    const active = item.to.includes("?") ? inReview : isActive && !(item.to === "/content" && inReview);
    return `nav-link${active ? " active" : ""}`;
  };
  const searchRef = useRef<HTMLInputElement>(null);

  // "/" focuses the search box from anywhere (except while typing in a field).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (event.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) && !target.isContentEditable) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const onSearch = (event: FormEvent) => {
    event.preventDefault();
    navigate(`/content?search=${encodeURIComponent(query.trim())}`);
  };

  if (!user) return null;

  return (
    <div className="shell">
      <aside className="sidebar" aria-label="Main navigation">
        <div className="sidebar__brand">
          <img src="/brand/logo.png" alt="Aviox Blogs" />
        </div>

        {NAV.map((group) => {
          const items = group.items.filter((item) => can(item.minimum));
          if (!items.length) return null;
          return (
            <nav key={group.label} className="sidebar__group" aria-label={group.label}>
              <p className="sidebar__label">{group.label}</p>
              {items.map((item) => {
                const count = item.count?.(dashboard.data);
                return (
                  <NavLink key={item.to} to={item.to} end={item.end} className={linkClass(item)}>
                    <item.icon size={18} />
                    <span>{item.label}</span>
                    {count ? <span className="nav-link__count">{count}</span> : null}
                  </NavLink>
                );
              })}
            </nav>
          );
        })}

        <div className="sidebar__footer">
          <div className="role-card">
            <p className="role-card__name">{user.first_name || user.username}</p>
            <p className="role-card__role">{ROLE_LABEL[user.role]}</p>
          </div>
          <button className="nav-link" style={{ border: 0, background: "none", cursor: "pointer" }} onClick={logout} type="button">
            <LogOut size={18} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <form className="topbar__search" onSubmit={onSearch} role="search">
            <Search size={16} />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search content by title, link or author"
              aria-label="Search content"
            />
            <kbd>/</kbd>
          </form>
          <div className="topbar__spacer" />
          <span className="muted" style={{ fontWeight: 600 }}>
            {user.email}
          </span>
        </header>
        <main className="page" id="main">
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}

/** Renders the page only for this role and above; otherwise explains why not. */
export function RequireRole({ minimum, children }: { minimum: Role; children: ReactNode }) {
  const { can } = useAuth();
  if (can(minimum)) return <>{children}</>;
  return (
    <div className="card">
      <Empty title="This area isn’t part of your role" icon={<ShieldCheck size={24} />}>
        It needs the <strong>{ROLE_LABEL[minimum]}</strong> role or higher. Ask a Super Admin if you need access.
      </Empty>
    </div>
  );
}
